import React from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Tooltip,
  IconButton,
} from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UndoIcon from '@mui/icons-material/Undo';
import NotificationsIcon from '@mui/icons-material/Notifications';
import BackspaceIcon from '@mui/icons-material/Backspace';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';
import type { Report } from '../../SearchArea/WeeklyMasterProvider';
import CustomSwitch from '../CustomSwitch';
import ImageAddDialog, { type ImageAddCandidate } from '../../../common/ImageAddDialog/ImageAddDialog';
import ImageAttachmentsGrid from '../../../common/ImageAddDialog/ImageAttachmentsGrid';
import { buildRespondentsTexts } from '../helpers/respondents';
import { formatAttrValueForDisplay } from '../helpers/attributes';
import { resolveWeeklyPeriod } from '../helpers/period';
import { buildRowIdentity } from '../helpers/rowIdentity';
import { resolveRowLockState, type RowLockOwner } from '../helpers/lockState';
import type { ImageAttachment } from '../../../../types/imageAttachment';
import type { SxProps, Theme } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import type { PublicScopeDisplay } from '../../../../lib/weeklyReportPublicScopeDisplay';

// Single result-row renderer.
// Receives all computed state/actions from Content.tsx through `ctx`.

type SaveStatus = 'idle' | 'editing' | 'saving';
type EditField = 'title' | 'body' | 'feedback';

type ReportTypeOption = {
  id: number;
  label: string;
  value: string;
  color?: string | null;
  attr_id: number;
  department_id?: number | null;
  team_id?: number | null;
  order_number?: number | null;
};

type ParseSelection = {
  values: string[];
  normalized: string;
};

type AttrPair = {
  attr_name: string;
  attr_value: string;
};

type ImageDialogState = {
  key: string;
  wrId: number | null;
} | null;

type ImageDeleteTargetState = {
  key: string;
  id: string;
  wrId?: number | null;
} | null;

type FieldEndActionsComponentProps = {
  label: string;
  locked: boolean;
  saving?: boolean;
  synced: boolean;
  canRollback: boolean;
  onRollback: () => void;
};

type TranslatableDisplayFieldComponentProps = {
  value: string;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  onStartEdit?: (caretIndex: number) => void;
};

type TranslatableDisplayTextareaComponentProps = {
  value: string;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  onStartEdit?: (caretIndex: number) => void;
  minHeightPx?: number;
  paddingRightPx?: number;
};

type WeeklyReportRowCardComponentProps = {
  isLocked: boolean;
  isRowSaving: boolean;
  shouldHighlightRow: boolean;
  children: React.ReactNode;
};

// Contract between Content.tsx (state owner) and this row renderer.
export type WeeklyReportResultRowContext = {
  otherEditingByWrId: Record<number, string[]>;
  lockOwnerByWrId: Record<number, RowLockOwner>;
  selfLoginId?: string;
  userName?: string;
  titleStatuses: SaveStatus[];
  bodyStatuses: SaveStatus[];
  feedbackStatuses: Record<number, SaveStatus>;
  getRowReportTypeOptions: (departmentId?: number | null, teamId?: number | null) => ReportTypeOption[];
  getReportTypeScopeDisplay: (optionId: number | string) => PublicScopeDisplay;
  getReportTypeAttrId: (departmentId?: number | null, teamId?: number | null) => number | null;
  getReportTypeMultipleSelect: (
    departmentId?: number | null,
    teamId?: number | null,
    attrId?: number | null
  ) => boolean;
  reportSelections: Record<number, string>;
  reportTypeSavingByWrId: Record<number, boolean>;
  parseReportTypeSelection: (
    raw: string | null | undefined,
    r: Report,
    options?: ReportTypeOption[]
  ) => ParseSelection;
  normalizeReportTypeToken: (v: unknown) => string;
  teamLabelMap: Record<number, string>;
  departmentLabelMap: Record<number, string>;
  teamBodyMaxLengthMap: Record<number, number>;
  teamBodyMaxLengthByValueMap: Record<string, number>;
  formatDateTime: (s: unknown) => string;
  getAttributesList: (r: Report) => AttrPair[];
  originalResults: Report[];
  equalIgnoringEdgeWhitespace: (a: string, b: string) => boolean;
  undoTitleMap: Record<number, string>;
  undoBodyMap: Record<number, string>;
  feedbackTexts: Record<number, string>;
  originalFeedbackTexts: Record<number, string>;
  undoFeedbackMap: Record<number, string>;
  communicationCountsByWrId: Record<number, number>;
  communicationNoticeByWrId: Record<number, boolean>;
  imageUploadByKey: Record<string, boolean>;
  versionSaving: Record<number, boolean>;
  imageAttachmentsByKey: Record<string, ImageAttachment[]>;
  fileAttachmentsByKey: Record<string, ImageAttachment[]>;
  refreshImageAttachmentUrl: (key: string, image: ImageAttachment) => Promise<ImageAttachment | null>;
  copyWrIdUrl: (wrId?: number | null) => void | Promise<void>;
  handleClickSaveVersion: (wrId?: number) => void | Promise<void>;
  openHistorySidebar: (wrId?: number) => void;
  setConfirmTargetIdx: React.Dispatch<React.SetStateAction<number | null>>;
  setConfirmTargetField: React.Dispatch<React.SetStateAction<'title' | 'body' | null>>;
  setConfirmOpen: React.Dispatch<React.SetStateAction<boolean>>;
  StatusIndicator: React.ComponentType<{ status: SaveStatus; locked?: boolean }>;
  RowSavingBadge: React.ComponentType<{ active: boolean }>;
  SelfEditingBadge: React.ComponentType<{ active: boolean; label?: string }>;
  FieldEndActions: React.ComponentType<FieldEndActionsComponentProps>;
  TranslatableDisplayField: React.ComponentType<TranslatableDisplayFieldComponentProps>;
  LinkifyDisplay: React.ComponentType<{ text: string }>;
  TranslatableDisplayTextarea: React.ComponentType<TranslatableDisplayTextareaComponentProps>;
  updateTitleStatus: (idx: number, status: SaveStatus) => void;
  pendingTitleCaretRef: React.MutableRefObject<Record<number, number>>;
  setTitleInputRef: (rowKeyNum: number) => (el: HTMLTextAreaElement | null) => void;
  startEdit: (wrId?: number, field?: EditField) => void;
  handleBlur: (idx: number, field: 'title' | 'body', nextValue?: string) => void | Promise<void>;
  stopEdit: (wrId?: number, field?: EditField) => void;
  LOCKED_BG: string;
  SAVING_BG: string;
  SAVING_SHADOW: string;
  TITLE_ACTIVE_TEXT_SX: Record<string, unknown>;
  LOCKED_FG: string;
  BODY_TEXT_COLOR: string;
  FIELD_ACTION_PADDING_RIGHT_PX: number;
  updateBodyStatus: (idx: number, status: SaveStatus) => void;
  setBodyInputRef: (rowKeyNum: number) => (el: HTMLTextAreaElement | null) => void;
  autoResizeTextarea: (el: HTMLTextAreaElement | null) => void;
  scheduleScrollCaretIntoView: (el: HTMLTextAreaElement | null) => void;
  bodyInputRefs: React.MutableRefObject<Record<number, HTMLTextAreaElement | null>>;
  getTextareaFromEvent: (e: React.SyntheticEvent) => HTMLTextAreaElement | null;
  handleResizeStart: (e: React.SyntheticEvent) => void;
  handleResizeEnd: (e: React.SyntheticEvent) => void;
  getCaretIndexFromPoint: (root: HTMLElement, clientX: number, clientY: number) => number;
  pendingBodyCaretRef: React.MutableRefObject<Record<number, number>>;
  setImageDialogKey: React.Dispatch<React.SetStateAction<ImageDialogState>>;
  setImageDeleteTarget: React.Dispatch<React.SetStateAction<ImageDeleteTargetState>>;
  RIGHT_COL_SX: SxProps<Theme>;
  BTN_ROW_SX: SxProps<Theme>;
  requiredItems: unknown;
  changeReport: (idx: number, clickedValue: string, isMulti: boolean) => void | Promise<void>;
  FEEDBACK_LABEL_SX: SxProps<Theme>;
  TEXTAREA_WRAP_SX: SxProps<Theme>;
  rollbackFeedback: (wrId?: number, idx?: number) => void | Promise<void>;
  updateFeedbackStatus: (wrId: number | undefined, status: SaveStatus) => void;
  pendingFeedbackCaretRef: React.MutableRefObject<Record<number, number>>;
  setFeedbackInputRef: (rowKeyNum: number) => (el: HTMLTextAreaElement | null) => void;
  handleFeedbackFocus: (wrId?: number) => void;
  handleFeedbackBlur: (wrId?: number, nextValue?: string) => void | Promise<void>;
  handleFeedbackInputChange: (wrId: number | undefined, idx: number | undefined, value: string) => void;
  toggleSync: (idx: number, next?: boolean) => void;
  syncEnabledList: boolean[];
  isEnlightSyncAvailableForTeam: (teamId?: number | null) => boolean;
  enlightSyncUnavailableReason: string;
  rollbackTitle: (idx: number, wrId?: number | string) => void | Promise<void>;
  rollbackBody: (idx: number, wrId?: number | string) => void | Promise<void>;
  imageDialogKey: ImageDialogState;
  handleAddImages: (candidates: ImageAddCandidate[]) => void | Promise<void>;
  HEADER_ACTION_GROUP_SX: SxProps<Theme>;
  HEADER_ACTION_BUTTON_SX: SxProps<Theme>;
  HEADER_ICON_STROKE_SX: SxProps<Theme>;
  HEADER_CAPTION_SX: SxProps<Theme>;
  HEADER_ICON_STROKE_RED_SX: SxProps<Theme>;
  HEADER_CAPTION_RED_SX: SxProps<Theme>;
  ARTICLES_HEADER_SX: SxProps<Theme>;
  isBrowserTranslated: boolean;
  BODY_MIN_ROWS: number;
  WeeklyReportRowCard: React.ComponentType<WeeklyReportRowCardComponentProps>;
};

type WeeklyReportResultRowProps = {
  r: Report;
  idx: number;
  ctx: WeeklyReportResultRowContext;
};

const countUsableReportImageFiles = (report: Report): number => {
  const files = Array.isArray((report as any)?.files) ? ((report as any).files as any[]) : [];
  return files.filter((file) => {
    const status = String(file?.status ?? '').trim().toUpperCase();
    return !status || status === 'Y';
  }).length;
};

export const WeeklyReportResultRow: React.FC<WeeklyReportResultRowProps> = ({ r, idx, ctx }) => {
  const { palette } = useTheme();
  const isDark = palette.mode === 'dark';
  const {
    otherEditingByWrId,
    lockOwnerByWrId,
    selfLoginId,
    userName,
    titleStatuses,
    bodyStatuses,
    feedbackStatuses,
    getRowReportTypeOptions,
    getReportTypeScopeDisplay,
    getReportTypeAttrId,
    getReportTypeMultipleSelect,
    reportSelections,
    reportTypeSavingByWrId,
    parseReportTypeSelection,
    normalizeReportTypeToken,
    teamLabelMap,
    departmentLabelMap,
    teamBodyMaxLengthMap,
    teamBodyMaxLengthByValueMap,
    formatDateTime,
    getAttributesList,
    originalResults,
    equalIgnoringEdgeWhitespace,
    undoTitleMap,
    undoBodyMap,
    feedbackTexts,
    originalFeedbackTexts,
    undoFeedbackMap,
    communicationCountsByWrId,
    communicationNoticeByWrId,
    imageUploadByKey,
    versionSaving,
    imageAttachmentsByKey,
    fileAttachmentsByKey,
    refreshImageAttachmentUrl,
    copyWrIdUrl,
    handleClickSaveVersion,
    openHistorySidebar,
    setConfirmTargetIdx,
    setConfirmTargetField,
    setConfirmOpen,
    StatusIndicator,
    RowSavingBadge,
    SelfEditingBadge,
    FieldEndActions,
    TranslatableDisplayField,
    LinkifyDisplay,
    TranslatableDisplayTextarea,
    updateTitleStatus,
    pendingTitleCaretRef,
    setTitleInputRef,
    startEdit,
    handleBlur,
    stopEdit,
    LOCKED_BG,
    SAVING_BG,
    SAVING_SHADOW,
    TITLE_ACTIVE_TEXT_SX,
    LOCKED_FG,
    BODY_TEXT_COLOR,
    FIELD_ACTION_PADDING_RIGHT_PX,
    updateBodyStatus,
    setBodyInputRef,
    autoResizeTextarea,
    scheduleScrollCaretIntoView,
    bodyInputRefs,
    getTextareaFromEvent,
    handleResizeStart,
    handleResizeEnd,
    getCaretIndexFromPoint,
    pendingBodyCaretRef,
    setImageDialogKey,
    setImageDeleteTarget,
    RIGHT_COL_SX,
    BTN_ROW_SX,
    requiredItems,
    changeReport,
    FEEDBACK_LABEL_SX,
    TEXTAREA_WRAP_SX,
    rollbackFeedback,
    updateFeedbackStatus,
    pendingFeedbackCaretRef,
    setFeedbackInputRef,
    handleFeedbackFocus,
    handleFeedbackBlur,
    handleFeedbackInputChange,
    toggleSync,
    syncEnabledList,
    isEnlightSyncAvailableForTeam,
    enlightSyncUnavailableReason,
    rollbackTitle,
    rollbackBody,
    imageDialogKey,
    handleAddImages,
    HEADER_ACTION_GROUP_SX,
    HEADER_ACTION_BUTTON_SX,
    HEADER_ICON_STROKE_SX,
    HEADER_CAPTION_SX,
    HEADER_ICON_STROKE_RED_SX,
    HEADER_CAPTION_RED_SX,
    ARTICLES_HEADER_SX,
    isBrowserTranslated,
    BODY_MIN_ROWS,
    WeeklyReportRowCard,
  } = ctx;
  const wrId = r.wr_id;
  const {
    wrNum,
    rowKeyNum,
    rowKey,
    imageKey: imageEditKey,
    safeWrId,
  } = buildRowIdentity(wrId, idx);
  const { isLocked, lockedName } = resolveRowLockState({
    wrNum,
    otherEditingByWrId,
    lockOwnerByWrId,
    selfLoginId,
    userName,
  });
  
  const titleStatus = titleStatuses[idx] ?? 'idle';
  const bodyStatus = bodyStatuses[idx] ?? 'idle';
  const feedbackStatus = wrId ? (feedbackStatuses[wrNum] ?? 'idle') : 'idle';
  const titleValue = r.title ?? '';
  const bodyValue = r.body ?? '';
  const feedbackValue = Number.isFinite(wrNum)
    ? (feedbackTexts[wrNum] ?? '')
    : '';
  const [draftTitle, setDraftTitle] = React.useState(titleValue);
  const [draftBody, setDraftBody] = React.useState(bodyValue);
  const [draftFeedback, setDraftFeedback] = React.useState(feedbackValue);
  const draftTitleRef = React.useRef(titleValue);
  const draftBodyRef = React.useRef(bodyValue);
  const draftFeedbackRef = React.useRef(feedbackValue);
  const bodyResizeRafRef = React.useRef<number | null>(null);

  const updateDraftTitle = React.useCallback((next: string) => {
    if (draftTitleRef.current === next) return;
    draftTitleRef.current = next;
    setDraftTitle(next);
  }, []);

  const updateDraftBody = React.useCallback((next: string) => {
    if (draftBodyRef.current === next) return;
    draftBodyRef.current = next;
    setDraftBody(next);
  }, []);

  const setDraftFeedbackLocal = React.useCallback((next: string) => {
    if (draftFeedbackRef.current === next) return;
    draftFeedbackRef.current = next;
    setDraftFeedback(next);
  }, []);

  const updateDraftFeedback = React.useCallback((next: string) => {
    if (draftFeedbackRef.current === next) return;
    draftFeedbackRef.current = next;
    setDraftFeedback(next);
    handleFeedbackInputChange(wrNum, idx, next);
  }, [handleFeedbackInputChange, idx, wrNum]);

  React.useEffect(() => {
    if (titleStatus === 'editing') return;
    updateDraftTitle(titleValue);
  }, [titleStatus, titleValue, updateDraftTitle]);

  React.useEffect(() => {
    if (bodyStatus === 'editing') return;
    updateDraftBody(bodyValue);
  }, [bodyStatus, bodyValue, updateDraftBody]);

  React.useEffect(() => {
    if (feedbackStatus === 'editing') return;
    setDraftFeedbackLocal(feedbackValue);
  }, [feedbackStatus, feedbackValue, setDraftFeedbackLocal]);

  React.useEffect(() => {
    return () => {
      if (bodyResizeRafRef.current != null) {
        cancelAnimationFrame(bodyResizeRafRef.current);
      }
    };
  }, []);

  const scheduleBodyAutoResize = React.useCallback(
    (el: HTMLTextAreaElement | null) => {
      if (!el) return;
      if (bodyResizeRafRef.current != null) {
        cancelAnimationFrame(bodyResizeRafRef.current);
      }
      bodyResizeRafRef.current = requestAnimationFrame(() => {
        bodyResizeRafRef.current = null;
        autoResizeTextarea(el);
        scheduleScrollCaretIntoView(el);
      });
    },
    [autoResizeTextarea, scheduleScrollCaretIntoView]
  );

  const titleInputRef = React.useMemo(
    () => setTitleInputRef(rowKeyNum),
    [rowKeyNum, setTitleInputRef]
  );
  const bodyInputRef = React.useMemo(
    () => setBodyInputRef(rowKeyNum),
    [rowKeyNum, setBodyInputRef]
  );
  const feedbackInputRef = React.useMemo(
    () => setFeedbackInputRef(rowKeyNum),
    [rowKeyNum, setFeedbackInputRef]
  );
  const handleBodyInputRef = React.useCallback(
    (el: HTMLTextAreaElement | null) => {
      bodyInputRef(el);
      if (el) {
        scheduleBodyAutoResize(el);
      }
    },
    [bodyInputRef, scheduleBodyAutoResize]
  );
  
  const isTitleSaving = titleStatus === 'saving';
  const isBodySaving = bodyStatus === 'saving';
  const isFeedbackSaving = feedbackStatus === 'saving';
  const isSelfEditing =
    titleStatus === 'editing' ||
    bodyStatus === 'editing' ||
    feedbackStatus === 'editing';
  const editingBadgeLabel = isLocked
    ? (lockedName ? `${lockedName}が編集中` : '編集中')
    : (isSelfEditing ? '自分が編集中' : '');
  const rtOptions = getRowReportTypeOptions(r.department, r.team);
  const reportTypeAttrId = getReportTypeAttrId(r.department, r.team);
  const isReportTypeMulti = getReportTypeMultipleSelect(
    r.department,
    r.team,
    reportTypeAttrId
  );
  const selectedRaw = Number.isFinite(wrNum) ? (reportSelections[wrNum] ?? '') : '';
  const isReportTypeSaving = Number.isFinite(wrNum) ? !!reportTypeSavingByWrId[wrNum] : false;
  const selection = parseReportTypeSelection(selectedRaw, r, rtOptions);
  const selectedValues = isReportTypeMulti
    ? selection.values
    : (selection.values.length > 0 ? [selection.values[0]] : []);
  const selectedSet = new Set(
    selectedValues.map((v) => normalizeReportTypeToken(v))
  );
  const hasSelectedInOptions =
    selectedValues.length > 0 &&
    rtOptions.some((o) =>
      selectedSet.has(normalizeReportTypeToken(o.value))
    );
  const teamLabel = teamLabelMap[Number(r.team)] ?? (r.team != null ? String(r.team) : '');
  const departmentLabel = departmentLabelMap[Number(r.department)] ?? (r.department != null ? String(r.department) : '');
  const bodyCharCount = draftBody.length;
  const teamIdNum = Number(r.team);
  const teamIdFallbackNum = Number((r as any)?.team_id);
  const syncTargetTeamId = Number.isFinite(teamIdNum) ? teamIdNum : (Number.isFinite(teamIdFallbackNum) ? teamIdFallbackNum : null);
  const isEnlightSyncAvailable = isEnlightSyncAvailableForTeam(syncTargetTeamId);
  const isEnlightSyncDisabled = isLocked || !isEnlightSyncAvailable;
  let teamBodyMaxLen = Number.isFinite(teamIdNum)
    ? teamBodyMaxLengthMap[teamIdNum]
    : (Number.isFinite(teamIdFallbackNum) ? teamBodyMaxLengthMap[teamIdFallbackNum] : undefined);
  if (typeof teamBodyMaxLen !== 'number') {
    const key = r.team != null ? String(r.team) : '';
    teamBodyMaxLen = teamBodyMaxLengthByValueMap[key];
  }
  const hasBodyMaxLen = typeof teamBodyMaxLen === 'number' && Number.isFinite(teamBodyMaxLen) && teamBodyMaxLen > 0;
  const approve3 = r.approver ?? '';
  const approveam = r.approve_am ?? '';
  const createBy = r.create_by ?? '';
  const updateBy = r.update_by ?? '';
  const createDtRaw = (r as any)?.create_dt ?? (r as any)?.createDate ?? (r as any)?.create_date ?? (r as any)?.CREATE_DT ?? (r as any)?.CREATE_DATE;
  const updateDtRaw = (r as any)?.update_dt ?? (r as any)?.updateDate ?? (r as any)?.update_date ?? (r as any)?.UPDATE_DT ?? (r as any)?.UPDATE_DATE;
  const isUpdated = String(createDtRaw ?? '') !== String(updateDtRaw ?? '');
  const updateDtText = formatDateTime(updateDtRaw);
  const baseAttrPairs = getAttributesList(r);
  
  const period = resolveWeeklyPeriod(r);
  const start_date = period.startYmd;
  const end_date = period.endYmd;
  // 表示用（発行日ラベル）
  const issueDateText = period.text;
  
  // respondents（連名者）を取得
  const respondentsRaw: string[] = Array.isArray((r as any)?.respondents)
    ? (r as any).respondents.filter((v: unknown): v is string => (
      typeof v === 'string' && v.trim() !== ''
    ))
    : [];
  
  const { displayText: respondentsDisplayText, tooltipText: respondentsTooltipText } =
    buildRespondentsTexts(respondentsRaw, 2);
  
  const original = originalResults[idx];
  
  const hasUnsavedTitleDiff =
    !!original &&
    !equalIgnoringEdgeWhitespace(draftTitle, original.title ?? '');
  
  const hasUnsavedBodyDiff =
    !!original &&
    !equalIgnoringEdgeWhitespace(draftBody, original.body ?? '');
  
  const prevTitleSaved =
    Number.isFinite(wrNum) ? undoTitleMap[wrNum] : undefined;
  
  const prevBodySaved =
    Number.isFinite(wrNum) ? undoBodyMap[wrNum] : undefined;
  
  const hasPrevTitleSaved =
    typeof prevTitleSaved === 'string' &&
    !!original &&
    !equalIgnoringEdgeWhitespace(original.title ?? '', prevTitleSaved);
  
  const hasPrevBodySaved =
    typeof prevBodySaved === 'string' &&
    !!original &&
    !equalIgnoringEdgeWhitespace(original.body ?? '', prevBodySaved);
  
  const fbCurText = draftFeedback;
  const fbOrigText = Number.isFinite(wrNum)
    ? (originalFeedbackTexts[wrNum] ?? '')
    : '';
  // 直前のフィードバック
  const prevFbSaved = Number.isFinite(wrNum)
    ? undoFeedbackMap[wrNum]
    : undefined;
  
  // 未保存の差分があるか？
  const hasUnsavedFbDiff =
    !!wrNum &&
    !equalIgnoringEdgeWhitespace(fbCurText, fbOrigText);
  
  // 「直前保存前の値」が存在して、今の保存済み値と違うか？
  const hasPrevFbSaved =
    !!wrNum &&
    typeof prevFbSaved === 'string' &&
    !equalIgnoringEdgeWhitespace(fbOrigText, prevFbSaved);
  
  const feedbackCanRollback =
    !isLocked &&
    !!wrNum &&
    (hasUnsavedFbDiff || hasPrevFbSaved);
  const communicationCount = Number.isFinite(wrNum)
    ? communicationCountsByWrId[wrNum]
    : undefined;
  const hasCommunicationNotice = Number.isFinite(wrNum)
    ? !!communicationNoticeByWrId[wrNum]
    : false;
  
  const titleCanRollback =
    !isLocked &&
    !isTitleSaving &&
    (hasUnsavedTitleDiff || hasPrevTitleSaved);
  
  const bodyCanRollback =
    !isLocked &&
    !isBodySaving &&
    (hasUnsavedBodyDiff || hasPrevBodySaved);
  
  const isImageUploading = !!imageUploadByKey[imageEditKey];
  const isRowSaving =
    isTitleSaving ||
    isBodySaving ||
    isFeedbackSaving ||
    !!versionSaving[wrNum] ||
    isImageUploading;
  const shouldHighlightRow = isRowSaving || isSelfEditing || isLocked;
  const rowImages =
    imageAttachmentsByKey[imageEditKey] ?? fileAttachmentsByKey[imageEditKey] ?? [];
  const hasImageAttachmentState =
    Object.prototype.hasOwnProperty.call(imageAttachmentsByKey, imageEditKey) ||
    Object.prototype.hasOwnProperty.call(fileAttachmentsByKey, imageEditKey);
  const loadingImagePlaceholderCount =
    hasImageAttachmentState || rowImages.length > 0 ? 0 : countUsableReportImageFiles(r);
  const handleTitleBlurWithRelease = () => {
    void (async () => {
      try {
        await handleBlur(idx, 'title', draftTitleRef.current);
      } finally {
        stopEdit(wrId, 'title');
      }
    })();
  };
  const handleBodyBlurWithRelease = () => {
    void (async () => {
      try {
        await handleBlur(idx, 'body', draftBodyRef.current);
      } finally {
        stopEdit(wrId, 'body');
      }
    })();
  };
  const handleFeedbackBlurWithRelease = () => {
    void (async () => {
      try {
        await handleFeedbackBlur(wrNum, draftFeedbackRef.current);
      } finally {
        stopEdit(wrId, 'feedback');
      }
    })();
  };
  const handleTitleRollbackClick = () => {
    if (hasUnsavedTitleDiff) {
      updateDraftTitle(original?.title ?? '');
      updateTitleStatus(idx, 'idle');
      return;
    }
    void rollbackTitle(idx, wrId);
  };
  const handleBodyRollbackClick = () => {
    if (hasUnsavedBodyDiff) {
      updateDraftBody(original?.body ?? '');
      updateBodyStatus(idx, 'idle');
      return;
    }
    void rollbackBody(idx, wrId);
  };
  const handleFeedbackRollbackClick = () => {
    if (hasUnsavedFbDiff) {
      updateDraftFeedback(fbOrigText);
      updateFeedbackStatus(wrNum, 'idle');
      return;
    }
    void rollbackFeedback(wrNum, idx);
  };
  
            return (
  <WeeklyReportRowCard
    key={rowKey}
    isLocked={isLocked}
    isRowSaving={isRowSaving}
    shouldHighlightRow={shouldHighlightRow}
  >
      <Box
        sx={{
          ...ARTICLES_HEADER_SX,
          gridColumn: '1 / -1',
          ...(isDark ? { backgroundColor: '#1D3261' } : null),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
          <Tooltip title="URLをコピー" arrow>
            <span>
              <Button
                size="small"
                variant="text"
                disabled={!Number.isFinite(wrNum)}
                onClick={() => copyWrIdUrl(wrNum)}
                sx={{
                  minWidth: 0,
                  px: 0.5,
                  py: 0,
                  textTransform: 'none',
                  fontSize: 12,
                  fontWeight: 700,
                  textDecoration: 'underline',
                }}
              >
                {Number.isFinite(wrNum) ? `${wrNum}` : ''}
              </Button>
            </span>
          </Tooltip>
          <Box
            className="attr-list"
            sx={{
              mt: 0.75,
              ml: 0,
              display: 'flex',
              flexWrap: 'wrap',
              flex: '1 1 auto',
              minWidth: 0,
              overflow: 'hidden',
            }}
          >
            <span className="attr-tag" tag-title="課" title="課"><span className="attr-tag__val">{departmentLabel || ''}</span></span>
            {!!teamLabel?.trim() && (
              <span className="attr-tag" tag-title="チーム" title="チーム">
                <span className="attr-tag__val">{teamLabel}</span>
              </span>
            )}
              {baseAttrPairs
                .filter((p) => typeof p.attr_value === 'string' && p.attr_value.trim() !== '')
                .map((p, i) => (
                <span
                  key={`attr-${wrId}-${i}-${p.attr_name}-${p.attr_value}`}
                  className="attr-tag"
                  tag-title={p.attr_name}
                  title={p.attr_name}
                >
                  <span className="attr-tag__val">{formatAttrValueForDisplay(p)}</span>
                </span>
              ))}
          </Box>
          <Box
            sx={{
              ml: 'auto',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              minWidth: 0,
              flexShrink: 0,
            }}
          >
            <RowSavingBadge active={isRowSaving} />
            <SelfEditingBadge active={!!editingBadgeLabel} label={editingBadgeLabel} />
            {/* バージョン系のボックス */}
            <Box className="remand" sx={HEADER_ACTION_GROUP_SX}>
              <Tooltip title="バージョン保存" arrow>
                <Button
                  size="small"
                  variant="text"
                  sx={HEADER_ACTION_BUTTON_SX}
                  disabled={isLocked || !!versionSaving[Number(wrId)]}
                  onClick={() => handleClickSaveVersion(wrId)}
                >
                  <HistoryEduIcon
                    fontSize="small"
                    sx={HEADER_ICON_STROKE_SX}
                  />
                  <Typography
                    variant="caption"
                    sx={HEADER_CAPTION_SX}
                  >
                    バージョン保存
                  </Typography>
                </Button>
              </Tooltip>
  
              <Tooltip title="バージョン履歴" arrow>
                <Button
                  size="small"
                  variant="text"
                  sx={HEADER_ACTION_BUTTON_SX}
                  disabled={isLocked}
                  onClick={() => openHistorySidebar(wrId)}
                >
                  <WorkHistoryIcon
                    fontSize="small"
                    sx={HEADER_ICON_STROKE_SX}
                  />
                  <Typography
                    variant="caption"
                    sx={HEADER_CAPTION_SX}
                  >
                    バージョン履歴
                  </Typography>
                </Button>
              </Tooltip>
            </Box>
  
            {/* 差し戻しボックス（分離） */}
            <Box className="remand" sx={{ ...HEADER_ACTION_GROUP_SX, ml: 0.5 }}>
              <Tooltip title="週報を下書き状態に差し戻します" arrow>
                <Button
                  size="small"
                  variant="text"
                  sx={HEADER_ACTION_BUTTON_SX}
                  disabled={isLocked}
                  onClick={() => { setConfirmTargetIdx(idx); setConfirmTargetField(null); setConfirmOpen(true); }}
                >
                  <BackspaceIcon
                    fontSize="small"
                    color="error"
                    sx={HEADER_ICON_STROKE_RED_SX}
                  />
                  <Typography
                    variant="caption"
                    sx={HEADER_CAPTION_RED_SX}
                  >
                    下書きに差し戻す
                  </Typography>
                </Button>
              </Tooltip>
            </Box>
          </Box>
  
        </Box>
      </Box>
  
      <Box sx={{ pl: 2, pr: 0, py: 0 }}>
        {/* タイトル */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 0.5,
          }}
        >
          <StatusIndicator status={titleStatus} locked={isLocked} />
          {issueDateText && (
            <Typography variant="caption">
              発行日：<b>{issueDateText}</b>
            </Typography>
          )}
          <Typography variant="caption">
            記入者：<b>{createBy}</b>
          </Typography>
          {respondentsDisplayText && (
            <Tooltip title={`${respondentsTooltipText}`} arrow>
              <Typography
                variant="caption"
                sx={{
                  maxWidth: 360,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                連名者: <b>{respondentsDisplayText}</b>
              </Typography>
            </Tooltip>
          )}
          <Typography variant="caption">
            職制：<b>{approve3}</b>
          </Typography>
          <Typography variant="caption">
            AM：<b>{approveam}</b>
          </Typography>
        </Box>
  
        {/* タイトル（右端: 「タイトル + ✅ + Undo（戻るボタン）」） */}
        <Box aria-busy={isTitleSaving}>
          <Box sx={{ position: 'relative', width: '100%', minWidth: 0 }}>
            <Box sx={{ position: 'absolute', top: 4, right: 6, zIndex: 1 }}>
              <FieldEndActions
                label="タイトル"
                locked={isLocked}
                saving={isTitleSaving}
                synced={!!syncEnabledList[idx]}
                canRollback={titleCanRollback}
                onRollback={handleTitleRollbackClick}
              />
            </Box>
            {isBrowserTranslated && titleStatus !== 'editing' ? (
              <TranslatableDisplayField
                value={draftTitle}
                disabled={isLocked || isTitleSaving}
                placeholder="（未入力）"
                className={`wr-title-display titleField ${isLocked ? 'is-locked' : ''}`}
                  onStartEdit={(caretIndex: number) => {
                  if (isLocked || isTitleSaving) return;
  
                  // クリック位置を保存 → TextField 出現時にそこへ caret を合わせる
                  pendingTitleCaretRef.current[rowKeyNum] = caretIndex;
  
                  updateTitleStatus(idx, 'editing');
                  // startEdit(wrId);  // 二重カウント防止：focus側で startEdit する
                }}
              />
            ) : (
              <TextField
                fullWidth
                variant="outlined"
                value={draftTitle}
                multiline
                rows={2}
                className={`wr-title-textfield titleField${isLocked ? ' is-locked' : ''}`}
                disabled={isLocked || isTitleSaving}
                autoFocus={isBrowserTranslated && titleStatus === 'editing' && !isLocked && !isTitleSaving}
                inputRef={titleInputRef}
                onChange={(e) => updateDraftTitle(e.target.value)}
                onFocus={() => {
                  updateTitleStatus(idx, 'editing');
                  startEdit(wrId, 'title');
                }}
                onBlur={() => {
                  handleTitleBlurWithRelease();
                }}
                sx={{
                  mb: 0,
                  width: '100%',
                  backgroundColor: isLocked
                    ? LOCKED_BG
                    : (isTitleSaving ? SAVING_BG : 'inherit'),
                  boxShadow: isTitleSaving ? SAVING_SHADOW : 'none',
                  // dark 時は index.css の .MuiOutlinedInput-root !important が root 背景を覆うため内側を直接上書き
                  ...(isDark && !isLocked && isTitleSaving
                    ? { '& .MuiOutlinedInput-root': { backgroundColor: '#166534 !important' } }
                    : {}),
                  '& .MuiInputBase-input': {
                    fontFamily: 'monospace',
                    fontWeight: 700,
                    lineHeight: 1.25,
                    paddingTop: '6px',
                    paddingRight: '0px',
                    ...TITLE_ACTIVE_TEXT_SX,
                    ...(isLocked
                      ? { WebkitTextFillColor: 'currentColor' }
                      : undefined),
                  },
                }}
              />
            )}
          </Box>
        </Box>
        {/* 本文 */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mt: 0,
            mb: 1,
          }}
        >
          <StatusIndicator status={bodyStatus} locked={isLocked} />
        </Box>
  
        <Box aria-busy={isBodySaving}>
          {/* ptで空白を作らず、行として上に置く（余計な空白が消える） */}
          <Box sx={{ position: 'relative', width: '100%', minWidth: 0 }}>
            <Box sx={{ position: 'absolute', top: 4, right: 6, zIndex: 1 }}>
              <FieldEndActions
                label="本文"
                locked={isLocked}
                saving={isBodySaving}
                synced={!!syncEnabledList[idx]}
                canRollback={bodyCanRollback}
                onRollback={handleBodyRollbackClick}
              />
            </Box>
            {bodyStatus === 'editing' && !isLocked ? (
            // ✅ editing時の本文 TextField（置き換え案）
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
              <TextField
                fullWidth
                variant="outlined"
                multiline
                className="wr-body-textfield is-editing"
                minRows={BODY_MIN_ROWS}
                value={draftBody}
                disabled={isBodySaving}
                autoFocus
                inputRef={handleBodyInputRef}
                onChange={(e) => {
                  updateDraftBody(e.target.value);
                  const el = e.target as HTMLTextAreaElement;
                  scheduleBodyAutoResize(el);
                }}
                onFocus={() => {
                  updateBodyStatus(idx, 'editing');
                  scheduleBodyAutoResize(bodyInputRefs.current[rowKeyNum] ?? null);
                }}
                onKeyUp={(e) => {
                  scheduleScrollCaretIntoView(getTextareaFromEvent(e));
                }}
                onClick={(e) => {
                  scheduleScrollCaretIntoView(getTextareaFromEvent(e));
                }}
                onPointerDown={handleResizeStart}
                onPointerUp={handleResizeEnd}
                onBlur={() => {
                  handleBodyBlurWithRelease();
                }}
                sx={{
                  width: '100%',
                  '& .MuiInputBase-root': {
                    padding: 0,
                    alignItems: 'flex-start',
                  },
                  '& textarea': {
                    fontFamily: 'monospace',
                    lineHeight: 1.25,
                    color: BODY_TEXT_COLOR,
                    boxSizing: 'border-box',
  
                    // ✅ スクロールさせず伸ばす
                    overflow: 'hidden',
                    resize: 'vertical',
                    maxHeight: 'none',
                  },
                }}
              />
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  pr: 1.5,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '15px',
                    color: 'text.secondary',
                    opacity: 0.65,
                    lineHeight: 1,
                  }}
                >
                  {hasBodyMaxLen
                    ? `${bodyCharCount}/${teamBodyMaxLen}`
                    : `${bodyCharCount}`}
                </Typography>
              </Box>
            </Box>
  
            ) : (
              <Box
                className={`wr-display-field wr-body-display-root${isLocked ? ' is-locked' : ''}`}
                tabIndex={0}
                sx={{
                  pr: `${FIELD_ACTION_PADDING_RIGHT_PX}px`,
                  pt: '8px',
                  pb: '8px',
                  pl: '10px',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  ...(isLocked
                    ? { color: LOCKED_FG, backgroundColor: LOCKED_BG }
                    : undefined),
                  ...(isBodySaving
                    ? { boxShadow: SAVING_SHADOW }
                    : undefined),
                }}
                // mousedown時点で caretIndex を取ってから編集に切り替える
                onMouseDown={(e: React.MouseEvent) => {
                  if (isLocked) return;
  
                  // リンククリックは編集開始しない（リンクを開けるように）
                  const target = e.target as HTMLElement | null;
                  if (target?.closest('a')) return;
  
                  const container = e.currentTarget as HTMLElement;
                  const bodyText = String(draftBody ?? '');
                  const caretIndex =
                    bodyText.trim() === ''
                      ? 0
                      : getCaretIndexFromPoint(container, e.clientX, e.clientY);
  
                  // TextField 出現後に caret を合わせる
                  pendingBodyCaretRef.current[rowKeyNum] = caretIndex;
  
                  startEdit(wrId, 'body');
                  updateBodyStatus(idx, 'editing');
  
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  // 二重発火防止（編集開始は mousedown で実施済み）
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <LinkifyDisplay text={draftBody} />
              </Box>
            )}
          </Box>
        </Box>
  
        <Box sx={{ pt: 1, pb: 1, pr: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 2, alignItems: 'start' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minHeight: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Tooltip title="画像を追加" placement="top">
                  <span>
                    <Button
                      variant="outlined"
                      size="small"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        if (isLocked || isImageUploading) return;
                        setImageDialogKey({ key: imageEditKey, wrId: safeWrId });
                      }}
                      disabled={isImageUploading}
                      aria-disabled={isLocked || isImageUploading}
                      startIcon={<AddPhotoAlternateIcon fontSize="small" />}
                      sx={{ borderRadius: 2, minWidth: 0, px: 1.5, py: 0.5 }}
                    >
                      画像を追加
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </Box>
          <ImageAttachmentsGrid
            images={rowImages}
            loadingPlaceholderCount={loadingImagePlaceholderCount}
            onRemoveImage={(id) => {
              if (isLocked || isImageUploading) return;
              setImageDeleteTarget({ key: imageEditKey, id, wrId: safeWrId });
            }}
            editable={!isLocked}
            disableActions={isImageUploading}
            onRefreshImageUrl={(image) => refreshImageAttachmentUrl(imageEditKey, image)}
            lazy
            progressiveLoad
          />
        </Box>
      </Box>
  
      </Box>
  
        {/* 右側：分類ボタン・フィードバック */}
        <Box sx={{ ...RIGHT_COL_SX }}>
  
        <Box sx={BTN_ROW_SX}>
          {requiredItems == null ? (
            // ロード中のみ表示
            <Typography sx={{ px: 1, py: 0.5 }} color="text.secondary">
              Loading...
            </Typography>
          ) : (
            <>
              {(r.team == null || String(r.team).trim() === '') ? (
                // team が null/空のときはグレーで表示
                <>
                    {rtOptions.map((opt) => {
                    const isSelected = selectedSet.has(
                      normalizeReportTypeToken(opt.value)
                    );
                    const scopeDisplay = getReportTypeScopeDisplay(opt.id);
                    return (
                      <Tooltip
                        key={`${opt.id}-${opt.value}`}
                        title={scopeDisplay.tooltipText}
                        arrow
                        placement="top"
                      >
                      <Button
                        variant="contained"
                        onClick={() => {
                          if (isLocked || isReportTypeSaving) return;
                          changeReport(idx, opt.value, isReportTypeMulti);
                        }}
                        aria-disabled={isReportTypeSaving}
                        sx={{
                          border: isSelected
                            ? `2px solid ${isDark ? '#dbeafe' : '#ffffff'}`
                            : `1px solid ${isDark ? '#3b4f68' : 'rgba(0,0,0,0.18)'}`,
                          boxSizing: 'border-box',
                          backgroundColor: isSelected
                            ? (opt.color ? opt.color : undefined)
                            : (isDark ? '#26384f' : 'rgba(0,0,0,0.12)'),
                          color: isSelected && opt.color ? '#fff' : (isDark ? '#e2e8f0' : undefined),
                          boxShadow: isSelected && isDark
                            ? '0 0 0 1px rgba(96, 165, 250, 0.75)'
                            : 'none',
                          pointerEvents: isReportTypeSaving ? 'none' : 'auto',
                          '&:hover': {
                            backgroundColor: isSelected
                              ? (opt.color ? opt.color : undefined)
                              : (isDark ? '#314966' : 'rgba(0,0,0,0.18)'),
                            borderColor: isSelected
                              ? (isDark ? '#eff6ff' : '#ffffff')
                              : (isDark ? '#58708e' : 'rgba(0,0,0,0.28)'),
                          },
                          '&.Mui-focusVisible': {
                            outline: `2px solid ${isDark ? '#93c5fd' : '#2563eb'}`,
                            outlineOffset: 2,
                          },
                        }}
                      >
                        {opt.label}
                      </Button>
                      </Tooltip>
                    );
                  })}
                </>
              ) : (
                <>
                    {rtOptions.map((opt) => {
                    const isSelected = selectedSet.has(
                      normalizeReportTypeToken(opt.value)
                    );
                    const scopeDisplay = getReportTypeScopeDisplay(opt.id);
                    return (
                      <Tooltip
                        key={`${opt.id}-${opt.value}`}
                        title={scopeDisplay.tooltipText}
                        arrow
                        placement="top"
                      >
                      <Button
                        variant="contained"
                        onClick={() => {
                          if (isLocked || isReportTypeSaving) return;
                          changeReport(idx, opt.value, isReportTypeMulti);
                        }}
                        aria-disabled={isReportTypeSaving}
                        sx={{
                          border: isSelected
                            ? `2px solid ${isDark ? '#dbeafe' : '#ffffff'}`
                            : `1px solid ${isDark ? '#3b4f68' : 'rgba(0,0,0,0.18)'}`,
                          boxSizing: 'border-box',
                          backgroundColor: isSelected
                            ? (opt.color ? opt.color : undefined)
                            : (isDark ? '#26384f' : 'rgba(0,0,0,0.12)'),
                          color: isSelected && opt.color ? '#fff' : (isDark ? '#e2e8f0' : undefined),
                          boxShadow: isSelected && isDark
                            ? '0 0 0 1px rgba(96, 165, 250, 0.75)'
                            : 'none',
                          pointerEvents: isReportTypeSaving ? 'none' : 'auto',
                          '&:hover': {
                            backgroundColor: isSelected
                              ? (opt.color ? opt.color : undefined)
                              : (isDark ? '#314966' : 'rgba(0,0,0,0.18)'),
                            borderColor: isSelected
                              ? (isDark ? '#eff6ff' : '#ffffff')
                              : (isDark ? '#58708e' : 'rgba(0,0,0,0.28)'),
                          },
                          '&.Mui-focusVisible': {
                            outline: `2px solid ${isDark ? '#93c5fd' : '#2563eb'}`,
                            outlineOffset: 2,
                          },
                        }}
                      >
                        {opt.label}
                      </Button>
                      </Tooltip>
                    );
                  })}
                </>
              )}
            </>
          )}
        </Box>
  
        {(isLocked || feedbackStatus !== 'idle') && (
          <Box
            sx={{
              ...FEEDBACK_LABEL_SX,
              gap: 1,
            }}
          >
            <StatusIndicator status={feedbackStatus} locked={isLocked} />
          </Box>
        )}
        <Box sx={TEXTAREA_WRAP_SX}>
          <Box sx={{ position: 'relative', width: '100%' }}>
            <Box
              sx={{
                position: 'absolute',
                top: 6,
                right: 8,
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                zIndex: 1,
              }}
            >
              <Typography variant="subtitle2" sx={{ lineHeight: 1, fontSize: '12px' }}>
                フィードバック
              </Typography>
              {!isLocked && syncEnabledList[idx] ? (
                <CheckCircleIcon sx={{ fontSize: 16 }} color="success" />
              ) : (
                <CheckCircleOutlineIcon sx={{ fontSize: 16 }} color="disabled" />
              )}
              <Tooltip title="変更を元に戻します" arrow>
                <span>
                  <IconButton
                    className="feedback-undo-button"
                    size="small"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={handleFeedbackRollbackClick}
                    disabled={!feedbackCanRollback}
                    sx={{
                      p: 0.2,
                      color: feedbackCanRollback
                        ? 'primary.main'
                        : 'action.disabled',
                    }}
                  >
                    <UndoIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
            {isBrowserTranslated && feedbackStatus !== 'editing' ? (
              <TranslatableDisplayTextarea
                value={draftFeedback}
                disabled={isLocked}
                placeholder="（未入力）"
                className={`wr-fb-display ${isLocked ? 'is-locked' : ''}`}
                // クリック位置 caret を保存してから editing にする
                  onStartEdit={(caretIndex: number) => {
                  if (isLocked) return;
                  pendingFeedbackCaretRef.current[rowKeyNum] = caretIndex;
                  updateFeedbackStatus(wrNum, 'editing');
                }}
                paddingRightPx={56}
                minHeightPx={241}
              />
            ) : (
              <TextField
                fullWidth
                variant="outlined"
                multiline
                minRows={5}
                className={`wr-fb-text${isLocked ? ' is-locked' : ''}`}
                value={draftFeedback}
                disabled={isLocked}
                autoFocus={isBrowserTranslated && feedbackStatus === 'editing' && !isLocked}
                // 表示側で拾った caret をここで反映
                inputRef={feedbackInputRef}
                onFocus={() => { handleFeedbackFocus(wrNum); startEdit(wrId, 'feedback'); }}
                onBlur={handleFeedbackBlurWithRelease}
                onChange={(e) => updateDraftFeedback(e.target.value)}
                slotProps={{
                  input: {
                    className: "FeedbackInputRoot",
                  },
                }}
                sx={{
                  backgroundColor: isLocked
                    ? LOCKED_BG
                    : (isFeedbackSaving ? SAVING_BG : 'inherit'),
                  boxShadow: isFeedbackSaving ? SAVING_SHADOW : 'none',
                  ...(isDark && !isLocked && isFeedbackSaving
                    ? { '& .MuiOutlinedInput-root': { backgroundColor: '#166534 !important' } }
                    : {}),
                  '& .MuiInputBase-input': {
                    WebkitTextFillColor: isLocked ? 'currentColor' : undefined,
                  },
                  '& .MuiInputBase-input.Mui-disabled': {
                    color: isDark ? '#e2e8f0' : BODY_TEXT_COLOR,
                    WebkitTextFillColor: isDark ? '#e2e8f0' : BODY_TEXT_COLOR,
                    opacity: 1,
                  },
                }}
              />
            )}
          </Box>
  
          {/* 週報コミュニケーションスレッド + Enlight同期（フィードバック欄の下） */}
          <Box
            sx={{
              mt: 0.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 1,
            }}
          >
            <Tooltip title="週報コミュニケーションスレッドを別タブで開く" arrow>
              {/* disabled時のTooltip維持用にspanでラップ */}
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    if (!wrId) return;
  
                    // 1) 渡したいペイロードを作成（必要な項目だけ）
                    const payload = {
                      wr_id: wrId,
                      title: r.title ?? '',
                      body: r.body ?? '',
                      department: departmentLabel ?? '',
                      team: teamLabel ?? '',
                      reporter: createBy ?? '',
                      reporter_mail: r.create_by_user_id ?? '',
                      updated_at: updateDtText ?? '',
                    };
  
                    // 2) 一時キーを作成して localStorage に保存（新タブが受け取り後に削除）
                    const key = `wr-thread:${wrId}:${Date.now()}`;
                    try {
                      localStorage.setItem(key, JSON.stringify(payload));
                    } catch (e) {
                      console.error('localStorage セット失敗:', e);
                    }
  
                    // 3) 別タブでスレッド画面を開く（安全のため noopener/noreferrer）
                    const url =
                      `/weekly-thread?wr_id=${encodeURIComponent(String(wrId))}` +
                      `&key=${encodeURIComponent(key)}`;
                    window.open(url, '_blank', 'noopener,noreferrer');
                  }}
                  disabled={!wrId}
                  sx={{
                    borderRadius: 2,
                    minWidth: 0,
                    px: 1.5,
                    py: 0.5,
                    ...(hasCommunicationNotice
                      ? {
                        color: 'error.main',
                        borderColor: 'error.main',
                        '&:hover': {
                          borderColor: 'error.dark',
                          backgroundColor: 'rgba(211, 47, 47, 0.08)',
                        },
                      }
                      : undefined),
                  }}
                >
                  {hasCommunicationNotice && (
                    <NotificationsIcon sx={{ fontSize: 16, mr: 0.5 }} />
                  )}
                  週報コミュニケーションスレッド
                  {communicationCount ? ` (${communicationCount})` : ''}
                </Button>
              </span>
            </Tooltip>
  
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              onClick={() => {
                if (isEnlightSyncDisabled) return;
                toggleSync(idx);
              }}
            >
              <Typography variant="body2" sx={{ fontSize: '13px' }}>
                Enlight同期
              </Typography>
              <Tooltip
                title={!isEnlightSyncAvailable ? enlightSyncUnavailableReason : ''}
                disableHoverListener={isEnlightSyncAvailable}
                disableFocusListener={isEnlightSyncAvailable}
                disableTouchListener={isEnlightSyncAvailable}
                arrow
              >
                <span>
                  <CustomSwitch
                    checked={!!syncEnabledList[idx]}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(_, checked) => {
                      if (isEnlightSyncDisabled) return;
                      toggleSync(idx, checked);
                    }}
                    color="success"
                    disabled={isEnlightSyncDisabled}
                    translateToEnglishOnTranslate
                  />
                </span>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </Box>
  
      <ImageAddDialog
        open={imageDialogKey?.key === imageEditKey}
        onClose={() => setImageDialogKey(null)}
        onAdd={handleAddImages}
        disableRestoreFocus
      />
  </WeeklyReportRowCard>
  );

};
